import { useEffect } from 'react';

interface ProductStructuredDataProps {
  name: string;
  description: string;
  image: string;
  price: number; // in kobo
  availability: boolean;
  category: string;
  url: string;
  sku?: string; // Product ID for unique identification
}

export function ProductStructuredData({ 
  name, 
  description, 
  image, 
  price, 
  availability,
  category,
  url,
  sku,
}: ProductStructuredDataProps) {
  useEffect(() => {
    const scriptId = 'product-structured-data';
    
    // Remove existing script if present
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name,
      description: description.slice(0, 200),
      image,
      url,
      ...(sku && { sku }),
      brand: {
        '@type': 'Brand',
        name: 'Nurture Skin Studio',
      },
      category,
      offers: {
        '@type': 'Offer',
        price: (price / 100).toFixed(2),
        priceCurrency: 'NGN',
        availability: availability
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        seller: {
          '@type': 'Organization',
          name: 'Nurture Skin Studio',
        },
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'NG',
          },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: {
              '@type': 'QuantitativeValue',
              minValue: 1,
              maxValue: 2,
              unitCode: 'DAY',
            },
            transitTime: {
              '@type': 'QuantitativeValue',
              minValue: 1,
              maxValue: 5,
              unitCode: 'DAY',
            },
          },
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: 'NG',
          returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: 7,
          returnMethod: 'https://schema.org/ReturnByMail',
          returnFees: 'https://schema.org/ReturnFeesCustomerResponsibility',
        },
      },
    });
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, [name, description, image, price, availability, category, url, sku]);

  return null;
}

// Product list schema for collection pages (Products, Featured)
interface ProductListItem {
  id: string;
  name: string;
  price: number;
  image_url?: string | null;
  description?: string | null;
  in_stock?: boolean;
}

interface ProductListStructuredDataProps {
  products: ProductListItem[];
}

export function ProductListStructuredData({ products }: ProductListStructuredDataProps) {
  useEffect(() => {
    const scriptId = 'product-list-structured-data';
    
    // Remove existing script if present
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    
    if (products.length === 0) return;
    
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Skincare Products - Nurture Skin Studio',
      description: 'Authentic skincare products from trusted global brands',
      numberOfItems: products.length,
      itemListElement: products.slice(0, 50).map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.name,
          url: `https://nurtureskinstudio.co/products/${product.id}`,
          ...(product.image_url && { image: product.image_url }),
          ...(product.description && { description: product.description.slice(0, 200) }),
          offers: {
            '@type': 'Offer',
            price: (product.price / 100).toFixed(2),
            priceCurrency: 'NGN',
            availability: product.in_stock !== false
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
          },
        },
      })),
    });
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, [products]);

  return null;
}

// LocalBusiness schema for the store
export function LocalBusinessStructuredData() {
  useEffect(() => {
    const scriptId = 'local-business-structured-data';
    
    // Remove existing script if present
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': 'https://nurtureskinstudio.co/#organization',
      name: 'Nurture Skin Studio',
      description: 'Authentic skincare products curated for your unique skin journey. Expert guidance, trusted global brands, and personalized skincare routines.',
      url: 'https://nurtureskinstudio.co',
      logo: 'https://storage.googleapis.com/gpt-engineer-file-uploads/ki8llOiWTicJGVKizVBgA5z4JMg2/uploads/1768514510646-575270915_17852002539575137_46209668038292982_n.jpg',
      image: 'https://storage.googleapis.com/gpt-engineer-file-uploads/ki8llOiWTicJGVKizVBgA5z4JMg2/uploads/1768514510646-575270915_17852002539575137_46209668038292982_n.jpg',
      telephone: '+234-706-573-2481',
      priceRange: '₦₦',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Lekki',
        addressLocality: 'Lagos',
        addressRegion: 'Lagos',
        addressCountry: 'NG',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '6.4698',
        longitude: '3.5852',
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '09:00',
          closes: '18:00',
        },
      ],
      sameAs: [
        'https://www.instagram.com/nurtureskinstudio_/',
      ],
    });
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, []);

  return null;
}

// WebSite schema with search action
export function WebSiteStructuredData() {
  useEffect(() => {
    const scriptId = 'website-structured-data';
    
    // Remove existing script if present
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Nurture Skin Studio',
      url: 'https://nurtureskinstudio.co',
      description: 'Authentic skincare products in Lagos, Nigeria. Expert guidance and trusted global brands.',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: 'https://nurtureskinstudio.co/products?search={search_term_string}',
        },
        'query-input': 'required name=search_term_string',
      },
    });
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, []);

  return null;
}

export function OrganizationStructuredData() {
  useEffect(() => {
    const scriptId = 'organization-structured-data';
    
    // Remove existing script if present
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Nurture Skin Studio',
      url: 'https://nurtureskinstudio.co',
      logo: 'https://storage.googleapis.com/gpt-engineer-file-uploads/ki8llOiWTicJGVKizVBgA5z4JMg2/uploads/1768514510646-575270915_17852002539575137_46209668038292982_n.jpg',
      description: 'Authentic skincare products curated for your unique skin journey. Expert guidance, trusted global brands, and personalized skincare routines.',
      sameAs: [
        'https://www.instagram.com/nurtureskinstudio_/',
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+234-706-573-2481',
        contactType: 'customer service',
        availableLanguage: 'English',
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Lekki',
        addressRegion: 'Lagos',
        addressCountry: 'NG',
      },
    });
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, []);

  return null;
}

interface BreadcrumbStructuredDataProps {
  items: Array<{ name: string; url: string }>;
}

export function BreadcrumbStructuredData({ items }: BreadcrumbStructuredDataProps) {
  useEffect(() => {
    const scriptId = 'breadcrumb-structured-data';
    
    // Remove existing script if present
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    });
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, [items]);

  return null;
}

interface BlogPostStructuredDataProps {
  title: string;
  description: string;
  image?: string;
  publishedAt: string;
  url: string;
}

export function BlogPostStructuredData({
  title,
  description,
  image,
  publishedAt,
  url,
}: BlogPostStructuredDataProps) {
  useEffect(() => {
    const scriptId = 'blogpost-structured-data';
    
    // Remove existing script if present
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: title,
      description: description.slice(0, 200),
      image: image || undefined,
      datePublished: publishedAt,
      url,
      author: {
        '@type': 'Organization',
        name: 'Nurture Skin Studio',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Nurture Skin Studio',
        logo: {
          '@type': 'ImageObject',
          url: 'https://storage.googleapis.com/gpt-engineer-file-uploads/ki8llOiWTicJGVKizVBgA5z4JMg2/uploads/1768514510646-575270915_17852002539575137_46209668038292982_n.jpg',
        },
      },
    });
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, [title, description, image, publishedAt, url]);

  return null;
}
